import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PlusCircleOutlined as AddCircle, QuestionCircleOutlined as HelpOutline } from '@ant-design/icons';
import { Button } from 'antd';

import Alert from 'components/Alert';
import { CONSOLE_URL } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import type { Webhook, WebhookFilter } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import WebhookContext from './context';
import { List } from './List';
import ModalConfirmWebhookCreation from './ModalConfirmWebhookCreation';
import UnifyDuplicatedUrl from './UnifyDuplicatedUrl';
import WebhookForm from './WebhookForm';
import WebhookInativationAlert from './WebhookInativationAlert';

const WEBHOOK_DOCUMENTATION_URL = 'https://eduzz.github.io/webhook';
const WEBHOOK_FAQ_URL = 'https://ajuda.eduzz.com/hc/pt-br/articles/4403004815643-O-que-%C3%A9-e-como-usar-o-Webhook-';

export default function Webhook2() {
  const history = useHistory();
  const [showModalConfirmWebhookCreation, setShowModalConfirmWebhookCreation] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>();
  const [webhooks, setWebhooks] = React.useState<Webhook[]>([]);
  const [selectedWebhook, setSelectedWebhook] = React.useState<Webhook>();
  const [isFormOpen, setIsFormOpen] = React.useState<boolean>(false);
  const [isActionsOpen, setIsActionsOpen] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();
  const [filter, setFilter] = React.useState<WebhookFilter>({ type: 'all', status: 'all' });

  const listWebhooks = async () => {
    try {
      setLoading(true);
      const { hasActiveUrls } = await webhookService.hasActiveUrls();

      if (!hasActiveUrls) {
        const confirm = await Alert.show({
          title: 'Crie e Gerencie Webhooks na DevHub',
          message:
            'Controle suas configurações e monitore o desempenho dos seus Webhooks e outras integrações. Acesse o console e ajuste suas preferências clicando no botão abaixo.',
          buttonOk: 'Ir para configurações',
          confirmation: true
        });

        if (confirm) {
          window.open(
            `${CONSOLE_URL}/webhook/configs?utm_source=referral&utm_medium=webhook&utm_campaign=myeduzz`,
            '_blank'
          );
          history.push('/producer/dashboard');
          return;
        }

        history.push('/producer/dashboard');
      }

      const webhooks = await webhookService.list();
      setWebhooks(webhooks);

      setLoading(false);
    } catch (e) {
      toast.error('Ocorreu um erro ao tentar carregar webhooks');
      setLoading(false);
    }
  };

  const showHideModalConfirmWebhookCreation = () => {
    setShowModalConfirmWebhookCreation(!showModalConfirmWebhookCreation);
  };

  const handleOpenForm = () => {
    setIsFormOpen(true);
  };

  return (
    <div id='webhook-v2-screen'>
      <WebhookContext.Provider
        value={{
          loading,
          webhooks,
          selectedWebhook,
          isFormOpen,
          isActionsOpen,
          anchorEl,
          filter,
          setAnchorEl,
          setFilter,
          setSelectedWebhook,
          setIsActionsOpen,
          setIsFormOpen,
          listWebhooks,
          setLoading
        }}
      >
        <UnifyDuplicatedUrl />
        <WebhookInativationAlert />

        <TopPage
          title='Webhooks'
          action={
            <>
              <Button
                id='webhook-v2-button-help'
                icon={<HelpOutline />}
                href={WEBHOOK_FAQ_URL}
                target='_blank'
                className='mr-3'
                type='text'
              >
                Precisa de ajuda?
              </Button>

              <Button href={WEBHOOK_DOCUMENTATION_URL} target='_blank' className='mr-3'>
                Documentação
              </Button>

              <PermissionHide action={'myeduzz:webhook:criar'}>
                <Button
                  id='webhook-v2-button-new'
                  type='primary'
                  disabled={loading}
                  icon={<AddCircle />}
                  onClick={showHideModalConfirmWebhookCreation}
                >
                  Novo webhook
                </Button>
              </PermissionHide>
            </>
          }
        />
        <List />
        <WebhookForm />
        <ModalConfirmWebhookCreation
          opened={showModalConfirmWebhookCreation}
          onClose={showHideModalConfirmWebhookCreation}
          onContinue={handleOpenForm}
        />
      </WebhookContext.Provider>
    </div>
  );
}
